<template>
  <Teleport to="body">
    <div
      class="notify-root"
      aria-live="polite"
      aria-relevant="additions removals"
    >
      <TransitionGroup name="notify" tag="div" class="notify-stack">
        <div
          v-for="n in notify.state.items"
          :key="n.id"
          class="notify-card"
          :class="`t-${n.type}`"
          role="status"
          @mouseenter="pause(n.id)"
          @mouseleave="resume(n.id)"
        >
          <div class="notify-row">
            <div class="notify-icon" aria-hidden="true">
              <span v-if="n.type === 'success'">✔</span>
              <span v-else-if="n.type === 'error'">✖</span>
              <span v-else-if="n.type === 'warning'">⚠</span>
              <span v-else>ℹ</span>
            </div>

            <div class="notify-content">
              <p v-if="n.title" class="notify-title">{{ n.title }}</p>
              <p class="notify-msg">{{ n.message }}</p>

              <div v-if="n.actions?.length" class="notify-actions">
                <button
                  v-for="(a, i) in n.actions"
                  :key="i"
                  class="notify-btn"
                  :class="a.variant === 'primary' ? 'primary' : 'ghost'"
                  @click="handleAction(n.id, a.onClick)"
                >
                  {{ a.label }}
                </button>
              </div>
            </div>

            <button
              class="notify-close"
              @click="close(n.id)"
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>

          <div v-if="progressMap[n.id] !== undefined" class="notify-progress">
            <div
              class="notify-bar"
              :style="{ transform: `scaleX(${progressMap[n.id]})` }"
            />
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, reactive } from "vue";
import { useNotify } from "@/components/Municipal/Composables/useNotify";

const notify = useNotify();

const timers = new Map();
const progressMap = reactive({});

function startAutoClose(id, duration) {
  if (!duration || duration <= 0) return;

  const t = { start: performance.now(), remaining: duration };
  timers.set(id, t);

  document.body.classList.add("flash-noti");
  setTimeout(() => {
    document.body.classList.remove("flash-noti");
  }, 200);

  const tick = () => {
    const now = performance.now();
    const elapsed = now - t.start;
    const left = Math.max(0, t.remaining - elapsed);
    progressMap[id] = left / duration;

    if (left <= 0) {
      close(id);
      return;
    }
    t.raf = requestAnimationFrame(tick);
  };

  t.raf = requestAnimationFrame(tick);
  t.timeout = setTimeout(() => close(id), duration);
}

function stopAutoClose(id) {
  const t = timers.get(id);
  if (!t) return;
  if (t.timeout) clearTimeout(t.timeout);
  if (t.raf) cancelAnimationFrame(t.raf);
}

function pause(id) {
  const t = timers.get(id);
  if (!t) return;

  const now = performance.now();
  const elapsed = now - t.start;
  t.remaining = Math.max(0, t.remaining - elapsed);

  stopAutoClose(id);
}

function resume(id) {
  const t = timers.get(id);
  const item = notify.state.items.find((x) => x.id === id);
  if (!t || !item) return;

  t.start = performance.now();
  const remaining = t.remaining;

  if (remaining <= 0) {
    close(id);
    return;
  }

  const total = item.duration;

  const tick = () => {
    const now = performance.now();
    const elapsed = now - t.start;
    const left = Math.max(0, remaining - elapsed);
    progressMap[id] = left / total;

    if (left <= 0) {
      close(id);
      return;
    }
    t.raf = requestAnimationFrame(tick);
  };

  t.raf = requestAnimationFrame(tick);
  t.timeout = setTimeout(() => close(id), remaining);
}

function close(id) {
  stopAutoClose(id);
  delete progressMap[id];
  timers.delete(id);
  notify.remove(id);
}

function handleAction(id, fn) {
  try {
    fn();
  } finally {
    close(id);
  }
}

// detectar nuevos items
let lastIds = new Set();

const interval = setInterval(() => {
  const current = new Set(notify.state.items.map((x) => x.id));

  notify.state.items.forEach((x) => {
    if (!lastIds.has(x.id)) {
      progressMap[x.id] = 1;
      startAutoClose(x.id, x.duration);
    }
  });

  lastIds = current;
}, 150);

onBeforeUnmount(() => {
  clearInterval(interval);
  timers.forEach((_, id) => stopAutoClose(id));
});
</script>

<style scoped>
.notify-root {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 99999;
  pointer-events: none;
}

.notify-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* CARD */
.notify-card {
  width: min(360px, calc(100vw - 32px));
  border-radius: 18px;

  background: rgba(25, 25, 25, 0.75);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);

  color: var(--bg-popup);

  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.45),
    inset 0 0 0 1px rgba(255, 255, 255, 0.06);

  overflow: hidden;
  pointer-events: auto;

  transform: scale(0.96);
  transition: all 0.25s ease;
}

.notify-card:hover {
  transform: scale(1) translateY(-2px);
  box-shadow:
    0 18px 40px rgba(0, 0, 0, 0.6),
    inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

/* ROW */
.notify-row {
  display: grid;
  grid-template-columns: 40px 1fr 30px;
  gap: 12px;
  padding: 14px;
  align-items: start;
}

/* ICON */
.notify-icon {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  font-size: 14px;

  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.08);

  transition: all 0.3s ease;
}

.flash-noti {
  animation: flashBg 0.2s ease;
}

@keyframes flashBg {
  from {
    background: rgba(255, 255, 255, 0.05);
  }
  to {
    background: transparent;
  }
}

/* COLORES POR TIPO */
.t-success .notify-icon {
  background: rgba(57, 219, 138, 0.2);
  box-shadow: 0 0 12px rgba(57, 219, 138, 0.5);
}
.t-error .notify-icon {
  background: rgba(255, 90, 90, 0.2);
  box-shadow: 0 0 12px rgba(255, 90, 90, 0.5);
}
.t-warning .notify-icon {
  background: rgba(255, 196, 74, 0.2);
  box-shadow: 0 0 12px rgba(255, 196, 74, 0.5);
}
.t-info .notify-icon {
  background: rgba(120, 170, 255, 0.2);
  box-shadow: 0 0 12px rgba(120, 170, 255, 0.5);
}

/* TEXT */
.notify-title {
  margin: 0;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.2px;
}

.notify-msg {
  margin: 4px 0 0 0;
  font-size: 13px;
  opacity: 0.9;
  line-height: 1.3;
}

/* ACTIONS */
.notify-actions {
  margin-top: 10px;
  display: flex;
  gap: 8px;
}

.notify-btn {
  border-radius: 10px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;

  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: var(--bg-popup);

  transition: all 0.2s ease;
}

.notify-btn:hover {
  background: rgba(255, 255, 255, 0.16);
}

.notify-btn.primary {
  background: linear-gradient(135deg, #4f8cff, #7aa7ff);
  color: var(--bg-popup);
}

.notify-btn.primary:hover {
  filter: brightness(1.1);
}

/* CLOSE */
.notify-close {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.notify-close:hover {
  color: var(--bg-popup);
  transform: scale(1.1);
}

/* PROGRESS */
.notify-progress {
  height: 3px;
  background: rgba(255, 255, 255, 0.08);
}

.notify-bar {
  height: 100%;
  transform-origin: left center;
  background: linear-gradient(90deg, #4f8cff, #7aa7ff);
}

/* ANIMACIONES */
.notify-enter-active {
  animation: notifyIn 0.35s ease;
}

.notify-leave-active {
  animation: notifyOut 0.25s ease forwards;
}

@keyframes notifyIn {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.92);
    filter: blur(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

@keyframes notifyOut {
  to {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
}
</style>
